/**
 * Pricing Service
 *
 * Domain service for price calculations.
 * Consolidates pricing logic from AvailabilityConfigurationModal.vue (onCalculateTotalPrice).
 *
 * This is a pure business logic service - no Vue/UI dependencies.
 */
import { translateApp } from "@/i18n";
// =============================================================================
// CONSTANTS
// =============================================================================
/** Default tax rate (13% - Costa Rica IVA) */
export const DEFAULT_TAX_RATE = 0.13;
/**
 * Coerce a possibly non-numeric input (e.g. the empty string a cleared
 * `v-model.number` field yields) into a finite number, falling back otherwise.
 * Without this a cleared price field turns every downstream `.toFixed()` call
 * into a TypeError and tears the modal down.
 */
function toFiniteNumber(value, fallback = 0) {
    const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
    return Number.isFinite(n) ? n : fallback;
}
// =============================================================================
// PRICING SERVICE
// =============================================================================
export const pricingService = {
    /**
     * Calculate total price from net price, commission, and tax
     * This is the main calculation used in AvailabilityConfigurationModal
     *
     * Formula: total = servicePrice + commission + tax
     * Where commission = (commissionAmount/100 * netPrice) for PERCENTAGE
     *       commission = commissionAmount for FIXED
     *       tax = (netPrice + commission) * taxRate
     * Per ADR-0003 commission and tax derive from the base service price only;
     * additional charges are payable but never part of the commission or tax base.
     */
    calculateTotalPrice(config) {
        const { totalPrice } = this.getPriceBreakdown(config);
        return totalPrice;
    },
    getChargeBreakdown(netPrice, charges = [], compoundPercentages = false) {
        const baseNetPrice = toFiniteNumber(netPrice);
        let compoundableSubtotal = baseNetPrice;
        let total = 0;
        const resolved = charges.map((charge) => {
            const compounds = charge.compoundsPercentage
                ?? charge.compounds_percentage
                ?? compoundPercentages;
            const base = compounds ? compoundableSubtotal : baseNetPrice;
            const computedAmount = this.round(charge.type === 'PERCENTAGE' ? base * (toFiniteNumber(charge.amount) / 100) : toFiniteNumber(charge.amount));
            if (compounds)
                compoundableSubtotal += computedAmount;
            total += computedAmount;
            return {
                ...charge,
                compoundsPercentage: compounds,
                computedAmount,
            };
        });
        return {
            charges: resolved,
            total: this.round(total),
            compoundableSubtotal: this.round(compoundableSubtotal),
        };
    },
    /**
     * Get full price breakdown
     * Per ADR-0003 commission and tax are calculated on the base net price only.
     * Additional charges are payable (part of servicePrice) but never enter the
     * commission or tax base.
     */
    getPriceBreakdown(config) {
        const { commissionType, extraCharges = [], chargePercentageCompoundingEnabled = false, } = config;
        const netPrice = toFiniteNumber(config.netPrice);
        const commissionAmount = toFiniteNumber(config.commissionAmount);
        const taxRate = toFiniteNumber(config.taxRate, DEFAULT_TAX_RATE);
        const chargeBreakdown = this.getChargeBreakdown(netPrice, extraCharges, chargePercentageCompoundingEnabled);
        const servicePrice = netPrice + chargeBreakdown.total;
        // Calculate commission first (needed for tax calculation).
        // ADR-0003: percentage commission is based on the net service price only,
        // never on additional charges.
        let commission = 0;
        if (commissionAmount > 0) {
            if (commissionType === 'PERCENTAGE') {
                commission = (commissionAmount / 100) * netPrice;
            }
            else {
                commission = commissionAmount;
            }
        }
        // ADR-0003: tax is calculated on the net service price plus commission only.
        const taxableAmount = netPrice + commission;
        const tax = taxableAmount * taxRate;
        return {
            netPrice,
            servicePrice,
            extraCharges: chargeBreakdown.charges,
            extraChargeTotal: chargeBreakdown.total,
            compoundableSubtotal: chargeBreakdown.compoundableSubtotal,
            tax,
            commission,
            totalPrice: servicePrice + commission + tax,
        };
    },
    /**
     * Calculate commission in currency (for display)
     * When commission is percentage, shows the actual amount
     */
    getCommissionInCurrency(netPrice, commissionAmount, commissionType) {
        const safeNet = toFiniteNumber(netPrice);
        const safeCommission = toFiniteNumber(commissionAmount);
        if (commissionType === 'PERCENTAGE') {
            return (safeCommission / 100) * safeNet;
        }
        return safeCommission;
    },
    /**
     * Round to 2 decimal places (banker's rounding via toFixed)
     */
    round(value) {
        return Math.round(value * 100) / 100;
    },
    /**
     * Format price for display
     */
    formatCurrency(value, decimals = 2) {
        return `$${value.toFixed(decimals)}`;
    },
    /**
     * Calculate price for overbooking scenario
     * When booking count exceeds standard capacity, overflow price applies
     */
    calculateOverbookingPrice(config) {
        const { basePrice, overflowPrice, standardCapacity, overcapacity, bookedCount } = config;
        if (bookedCount <= standardCapacity) {
            // Within standard capacity - use base price
            return basePrice * bookedCount;
        }
        if (bookedCount <= overcapacity) {
            // In overbooking range
            const standardTotal = basePrice * standardCapacity;
            const overflowCount = bookedCount - standardCapacity;
            const overflowTotal = overflowPrice * overflowCount;
            return standardTotal + overflowTotal;
        }
        // Over max capacity - should not happen
        return 0;
    },
    /**
     * Validate pricing inputs
     */
    validatePricing(config) {
        const errors = [];
        if (config.netPrice < 0) {
            errors.push(translateApp("calendar.validation.net_price_not_negative"));
        }
        if (config.commissionAmount < 0) {
            errors.push(translateApp("calendar.validation.commission_not_negative"));
        }
        if (config.commissionType === 'PERCENTAGE' && config.commissionAmount > 100) {
            // Warning, not error - might be intentional
            errors.push(translateApp("calendar.validation.commission_over_100"));
        }
        if (config.taxRate !== undefined && (config.taxRate < 0 || config.taxRate > 1)) {
            errors.push(translateApp("calendar.validation.tax_rate_range"));
        }
        return {
            valid: errors.filter((e) => !e.startsWith(translateApp("calendar.validation.warning_prefix"))).length === 0,
            errors,
        };
    },
    /**
     * Calculate net price from total (reverse calculation)
     * Useful when you know the final price and need to calculate net
     *
     * New formula (tax on net + commission):
     * - For PERCENTAGE: total = (net + net * commissionPercent) * (1 + taxRate)
     *                   total = net * (1 + commissionPercent) * (1 + taxRate)
     * - For FIXED: total = (net + commission) * (1 + taxRate)
     */
    calculateNetFromTotal(totalPrice, commissionAmount, commissionType, taxRate = DEFAULT_TAX_RATE) {
        if (commissionType === 'FIXED') {
            // total = (net + commission) * (1 + taxRate)
            // net + commission = total / (1 + taxRate)
            // net = (total / (1 + taxRate)) - commission
            return (totalPrice / (1 + taxRate)) - commissionAmount;
        }
        // PERCENTAGE:
        // total = net * (1 + commissionPercent) * (1 + taxRate)
        // net = total / ((1 + commissionPercent) * (1 + taxRate))
        const commissionPercent = commissionAmount / 100;
        return totalPrice / ((1 + commissionPercent) * (1 + taxRate));
    },
};
// =============================================================================
// DEFAULT EXPORT
// =============================================================================
export default pricingService;
